import { useEffect, useState, Fragment, SyntheticEvent } from 'react';
import { FlexContainer, Label, Switch, Input } from '@facephi/ui-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useSetRecoilState } from 'recoil';
import {
  useFacialAuth,
  useVocalAuth,
  useData,
  useConfig,
} from '../../providers';
import { Options, RoutesName, userInfoSchema, widgetTypes } from '../../states';
import {
  widgetTypeState,
  antispoofingState,
  newOperationIdState,
} from '../../states/atoms';
import { GoBackHeader } from '../goBackHeader';
import { AddBiometryModal, NoBiometryModal } from '../modals';
import { VoiceTypesSelection } from '../voiceSelection';
import {
  ConfigurationCard,
  ConfigurationCardElement,
  ConfigurationCardHeader,
  ConfigurationCardWrapper,
  ConfigurationWrapper,
  UserDataInputsWrapper,
} from './Styles';

export const Configuration = () => {
  const { t } = useTranslation();
  const { facialValidated } = useFacialAuth();
  const { vocalValidated } = useVocalAuth();
  const { resetStates } = useData();
  const {
    setActiveFacial,
    setActiveVocal,
    setUserName,
    setUserLastname,
    onRemoveRegisteredBiometry,
    onAddRegisteredBiometry,
    registeredBiometries,
    noBiometrySelected,
    checkFacial,
    checkVocal,
    userName,
    userLastname,
    customPhraseValid,
  } = useConfig();

  const navigate = useNavigate();
  const [widgetType, setWdigetType] = useRecoilState(widgetTypeState);
  const setNewOperationId = useSetRecoilState(newOperationIdState);

  const [showAddBiometryModal, setShowAddBiometryModal] =
    useState<boolean>(false);
  const [showNoBiometryModal, setShowNoBiometryModal] =
    useState<boolean>(false);
  const [antispoofing, setAntispoofing] = useRecoilState(antispoofingState);

  const {
    register,
    reset,
    formState: { errors: userInfoErrors },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(userInfoSchema),
  });

  const noUserInfo = !!Object.keys(userInfoErrors).length;
  const goBackRoutes =
    noUserInfo || noBiometrySelected || !customPhraseValid
      ? ''
      : RoutesName.home;

  const validationMap = {
    [widgetTypes.facial]: {
      setValidation: setActiveFacial,
      validated: facialValidated,
    },
    [widgetTypes.vocal]: {
      setValidation: setActiveVocal,
      validated: vocalValidated,
    },
  };

  useEffect(() => {
    if (noBiometrySelected) {
      setShowNoBiometryModal(true);
    }
  }, [noBiometrySelected]);

  useEffect(() => {
    reset({ userName, userLastName: userLastname });
  }, []);

  const handleInputChange = (
    event: SyntheticEvent<HTMLInputElement> | undefined
  ) => {
    if (event?.currentTarget?.name === 'userName') {
      setUserName(event?.currentTarget?.value);
    } else {
      setUserLastname(event?.currentTarget?.value);
    }
  };

  const onAddBiometry = () => {
    setShowAddBiometryModal(false);
    setNewOperationId('');
    resetStates();
    switch (widgetType) {
      case widgetTypes.vocal:
        navigate(RoutesName.voiceSelection);
        break;
      default:
        navigate(RoutesName.widget);
        break;
    }
  };

  const onChangeWidget = (value: boolean, origin: widgetTypes) => {
    const widgetInfo = validationMap[origin];
    widgetInfo.setValidation(value);

    if (value && !widgetInfo.validated) {
      setShowAddBiometryModal(true);
      setWdigetType(origin);
    }

    if (value && widgetInfo.validated) {
      onAddRegisteredBiometry(origin);
    }

    if (!value && registeredBiometries.includes(origin)) {
      onRemoveRegisteredBiometry(origin);
    }
  };

  const configurationOptions: Options[] = [
    {
      title: 'User name',
      element: [
        {
          onlyChildren: true,
          message: '',
          children: (
            <UserDataInputsWrapper>
              <Input
                type="text"
                label={String(t('Name'))}
                {...register('userName')}
                onKeyUp={handleInputChange}
                errorLabel={
                  userInfoErrors.userName && t(userInfoErrors.userName.message)
                }
              />
              <Input
                type="text"
                label={String(t('Last name'))}
                {...register('userLastName')}
                onKeyUp={handleInputChange}
                errorLabel={
                  userInfoErrors.userLastName &&
                  t(userInfoErrors.userLastName.message)
                }
              />
            </UserDataInputsWrapper>
          ),
        },
      ],
    },
    {
      title: 'Facial Validation',
      element: [
        {
          onlyChildren: false,
          message: 'Face',
          onChange: (value: boolean) => {
            onChangeWidget(value, widgetTypes.facial);
          },
          checked: checkFacial,
          idSwitch: 'Facial',
          disabled: noUserInfo,
        },
      ],
    },
    {
      title: 'Voice Validation',
      titleTestId: 'settings_voice_section_title_text',
      element: [
        {
          onlyChildren: false,
          padding: '1.6rem 1.6rem 0.8rem 1.6rem',
          message: 'Voice',
          messageTestId: 'settings_voice_description_text',
          onChange: (value: boolean) => {
            onChangeWidget(value, widgetTypes.vocal);
          },
          checked: checkVocal,
          idSwitch: 'Voice',
          switchTestId: 'settings_voice_description_switch',
          disabled: noUserInfo,
          children: (
            <ConfigurationCardWrapper flexDirection="column">
              <VoiceTypesSelection disabled={!checkVocal || noUserInfo} />
            </ConfigurationCardWrapper>
          ),
        },
      ],
    },
    {
      title: 'Antispoofing',
      element: [
        {
          onlyChildren: false,
          borderTop: true,
          message: 'Antispoof validation',
          onChange: (value: boolean) => {
            setAntispoofing(value);
          },
          checked: antispoofing,
          idSwitch: 'Antispoof',
          description: 'Check antispoof in the process.',
        },
      ],
    },
  ];

  return (
    <>
      <ConfigurationWrapper flexDirection="column" rowGap="3.2">
        <GoBackHeader
          route={goBackRoutes}
          linkLabel="Go back"
          title="Configuration"
          onClick={() => noBiometrySelected && setShowNoBiometryModal(true)}
        />
        {configurationOptions
          .filter(({ hide }) => !hide)
          .map((item, index) => (
            <ConfigurationCard as="ul" key={index}>
              <ConfigurationCardHeader
                forwardedAs="h2"
                fontSize="16"
                semibold
                testId={item.titleTestId}
              >
                {t(item.title)}
              </ConfigurationCardHeader>

              {item.element
                ?.filter(({ hide }) => !hide)
                .map((value, index) => (
                  <Fragment key={`fragment-${index}`}>
                    {!value.onlyChildren && (
                      <ConfigurationCardElement
                        key={index}
                        alignItems="center"
                        justifyContent="space-between"
                        forwardedAs="li"
                        $boxPadding={value.padding || '1.6rem'}
                        $borderTop={value.borderTop}
                      >
                        <FlexContainer flexDirection="column" rowGap="0.6">
                          <Label
                            fontSize="14"
                            semibold
                            testId={value.messageTestId}
                          >
                            {value.message && t(value.message)}
                            {value?.separator}
                            {value.subMessage && ` ${value.subMessage}`}
                          </Label>

                          {value.description && (
                            <Label fontSize="12" color="grey500">
                              {t(value.description)}
                            </Label>
                          )}
                        </FlexContainer>

                        {value.onChange && (
                          <Switch
                            id={value.idSwitch as string}
                            onChange={value.onChange}
                            checked={value.checked}
                            disabled={value.disabled}
                            testId={value.switchTestId}
                          />
                        )}
                      </ConfigurationCardElement>
                    )}
                    {value.children}
                  </Fragment>
                ))}
            </ConfigurationCard>
          ))}
        <AddBiometryModal
          show={showAddBiometryModal}
          onChangeShow={(showModal: boolean) =>
            setShowAddBiometryModal(showModal)
          }
          onAddBiometry={onAddBiometry}
        />
        <NoBiometryModal
          show={showNoBiometryModal}
          onChangeShow={(showModal) => setShowNoBiometryModal(showModal)}
        />
      </ConfigurationWrapper>
    </>
  );
};
